import axios from 'axios';
import cl from 'classnames';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Demo from './SkeletonDemo';
import Input from '../Input/Input';
import Constants from '../../Constants';
import FilterSelect from '../Select/FilterSelect';
import LocalitiesAutocomplete from '../Woosmap/LocalitiesAutocomplete';
import { tr } from '../utils/locale';
import markerIcon from '../../images/marker.png';
import markerIconAlt from '../../images/marker-alt.png';
import { createWoosmapMap } from '../Map/drawOnMap';
import 'resize-observer-polyfill/dist/ResizeObserver.global';

export default class SearchDemo extends Component {
    constructor(props) {
        super(props);
        this.defaultLocality = {
            description: 'London, United Kingdom',
            name: 'London',
            type: 'locality',
            location: { lat: 51.50732, lng: -0.12764746 },
        };
        this.state = {
            localities: null,
            selectedLocality: this.defaultLocality,
            data: {},
            error: null,
            maxStore: '5',
            tags: [],
            types: [],
        };
        this.mounted = true;
        this.map = null;
        this.markers = [];
        this.requestUrl = 'https://api.woosmap.com/stores/search/';
        this.demoRef = React.createRef();
    }

    componentDidMount() {
        this.displayMap();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    getRequestparams = (encode) => {
        const { selectedLocality, maxStore, tags, types } = this.state;
        const location = selectedLocality ? selectedLocality.location : {};

        let tagsFilter = [];
        for (let i = 0; i < tags.length; i++) {
            tagsFilter.push(`${i === 0 ? '(' : 'AND '}tag:"${tags[i].value}"`);
        }

        let typesFilter = [];
        for (let i = 0; i < types.length; i++) {
            typesFilter.push(`${i === 0 ? '(' : 'OR '}type:"${types[i].value}"`);
        }

        tagsFilter = tags.length > 0 ? `${tagsFilter.join(' ')})` : '';
        typesFilter = types.length > 0 ? `${typesFilter.join(' ')})` : '';

        let query = tagsFilter + (tagsFilter.length > 0 && typesFilter.length > 0 ? ' AND ' : '') + typesFilter;

        if (encode) {
            query = encodeURIComponent(query).replace(/\(/g, '%28').replace(/\)/g, '%29');
        }
        return {
            key: Constants.woosmapKey,
            lat: location.lat,
            lng: location.lng,
            stores_by_page: maxStore || '5',
            query,
        };
    };

    requestSearch = () => {
        axios
            .get(this.requestUrl, {
                params: this.getRequestparams(false),
            })
            .then((response) => {
                if (this.mounted) {
                    const { data } = response;
                    this.setState(
                        {
                            data: data || [],
                            error: null,
                        },
                        this.displayOnMap
                    );
                }
            })
            .catch((error) => {
                const errorResult = (error && error.response && error.response.data) || 'Unhandled error';
                this.setState({ error: errorResult });
            });
    };

    selectLocality = (locality) => {
        this.setState({ selectedLocality: locality }, this.requestSearch);
    };

    renderAutocompleteList = () => {
        const { localities } = this.state;
        if (!localities || localities.localities.length === 0) {
            return <div className="demo__empty">No locality found</div>;
        }
        const result = localities.localities.map((locality) => {
            const { selectedLocality } = this.state;
            return (
                <li key={locality.public_id}>
                    <button
                        type="button"
                        className={cl('btn', 'btn--link', {
                            selected: selectedLocality === locality,
                        })}
                        onClick={() => this.selectLocality(locality)}
                    >
                        {locality.description}
                    </button>
                </li>
            );
        });
        return <ul>{result}</ul>;
    };

    renderStoreList = () => {
        const { data } = this.state;
        const stores = data.features;
        if (!stores || stores.length === 0) {
            return <div className="demo__empty">No stores found</div>;
        }
        const result = stores.map((store) => (
            <li key={store.properties.store_id}>
                <span>{store.properties.name}</span>
            </li>
        ));
        return <ul>{result}</ul>;
    };

    displayOnMap = () => {
        if (this.map) {
            const { data, selectedLocality } = this.state;
            const { features } = data;

            const bounds = new window.woosmap.map.LatLngBounds();

            if (this.markers.length > 0) {
                this.markers.forEach((marker) => marker.setMap(null));
                this.markers = [];
            }

            if (features && features.length > 0) {
                features.forEach((store) => {
                    const [lng, lat] = store.geometry.coordinates;
                    bounds.extend(new window.woosmap.map.LatLng(lat, lng));
                    this.markers.push(
                        new window.woosmap.map.Marker({
                            icon: { url: markerIcon, scaledSize: { height: 46, width: 30 } },
                            position: { lat, lng },
                            map: this.map,
                        })
                    );
                });

                const location = selectedLocality?.location || this.defaultLocality.location;
                if (location) bounds.extend(new window.woosmap.map.LatLng(location.lat, location.lng));
            }

            if (selectedLocality && selectedLocality.location) {
                this.markers.push(
                    new window.woosmap.map.Marker({
                        icon: { url: markerIconAlt, scaledSize: { height: 46, width: 30 } },
                        position: selectedLocality.location,
                        map: this.map,
                    })
                );
            }

            this.map.fitBounds(bounds, { top: 70, bottom: 70, left: 70, right: 70 });
        }
    };

    displayMap = () => {
        if (this.timeoutMap) clearTimeout(this.timeoutMap);

        if (window.woosmap && window.woosmap.map && this.demoRef.current) {
            window.woosmap.map.config.setApiKey(Constants.woosmapKey);
            this.map = createWoosmapMap(this.demoRef.current.getMap());
            this.requestSearch();
        } else {
            this.timeoutMap = setTimeout(this.displayMap, 300);
        }
    };

    renderHeaderFilters = () => {
        const { selectedLocality } = this.state;
        const originInputs = {
            label: tr('near ... '),
            component: (
                <>
                    <LocalitiesAutocomplete
                        woosmapKey={Constants.woosmapKey}
                        defaultLocality={selectedLocality}
                        callback={(item) => {
                            this.setState(
                                {
                                    selectedLocality: item || null,
                                },
                                this.requestSearch
                            );
                        }}
                    />
                </>
            ),
        };

        const tagOptions = [
            {
                value: 'wheelchair_access',
                label: tr('Wheelchair Access'),
            },
            {
                value: 'in-store_wi-fi',
                label: tr('In Store Wifi'),
            },
            { value: 'parking', label: tr('Parking') },
            {
                value: 'schedule_a_demo',
                label: tr('Schedule a Demo'),
            },
            {
                value: 'reserve_and_collect',
                label: tr('Reserve & Collect'),
            },
            {
                value: 'customised_solutions',
                label: tr('Customized Solutions'),
            },
            { value: 'financing', label: tr('Financing') },
        ];

        const tagsFilter = {
            label: tr('with ...'),
            component: (
                <FilterSelect
                    options={tagOptions}
                    placeholder={tr('Features')}
                    onChange={(items) => this.setState({ tags: items }, this.requestSearch)}
                />
            ),
        };

        const typeOptions = [
            { value: 'bose_store', label: tr('Bose Store') },
            {
                value: 'bose_factory_store',
                label: tr('Bose Factory Store'),
            },
            {
                value: 'bose_reseller_store',
                label: tr('Bose Reseller Store'),
            },
        ];

        const typesFilter = {
            label: tr('Looking for a ...'),
            component: (
                <FilterSelect
                    placeholder={tr('Types')}
                    options={typeOptions}
                    onChange={(items) => this.setState({ types: items }, this.requestSearch)}
                />
            ),
        };

        return [typesFilter, originInputs, tagsFilter];
    };

    renderFooterFilters = () => {
        const { maxStore } = this.state;
        const maxStoreFilter = {
            label: tr('Maximum number of stores'),
            component: (
                <Input
                    size="small"
                    className="mini"
                    value={maxStore}
                    autocomplete={false}
                    placeholder={tr('Maximum stores...')}
                    onChange={(e) => this.setState({ maxStore: e.target.value }, this.requestSearch)}
                />
            ),
        };
        return [maxStoreFilter];
    };

    render() {
        const { data, error } = this.state;
        const { noheader } = this.props;
        const headerLabels = {
            title: tr('Store Search API'),
            desc: tr(
                'Search among your own Points of Interest. Find stores by geography, by attributes or by autocomplete on store names.'
            ),
        };
        return (
            <Demo
                id="search-demo"
                className="demo--search"
                docLink="https://developers.woosmap.com/products/search-api/get-started/"
                header={headerLabels}
                footerFilters={this.renderFooterFilters()}
                headerFilters={this.renderHeaderFilters()}
                request={this.requestUrl}
                params={this.getRequestparams(true)}
                referer="http://localhost/"
                response={error || data}
                noheader={noheader}
                result={this.renderStoreList()}
                withMap
                ref={this.demoRef}
            />
        );
    }
}

SearchDemo.defaultProps = {
    noheader: false,
};
SearchDemo.propTypes = {
    noheader: PropTypes.bool,
};
